import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowDown,
  faArrowLeft,
  faArrowRight,
  faArrowUp,
  faTimes
} from '@fortawesome/free-solid-svg-icons';
import {
  DEFAULT_FLYER_PHOTO_PLACEMENT,
  normalizeFlyerPhotoPlacement
} from './BoardResultsFlyerPoster';
import './BoardResultsFlyerPhotoEditor.css';

const NUDGE = 4;

/**
 * Move whole student block (photo + roll + name + marks) and/or crop the face.
 */
export default function BoardResultsFlyerPhotoEditor({
  row,
  photoUrl,
  placement,
  onSave,
  onClose
}) {
  const [draft, setDraft] = useState(() => normalizeFlyerPhotoPlacement(placement));
  const frameRef = useRef(null);
  const dragRef = useRef(null);

  useEffect(() => {
    setDraft(normalizeFlyerPhotoPlacement(placement));
  }, [placement, row?.id]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setDraft((prev) => normalizeFlyerPhotoPlacement({ ...prev, ox: prev.ox - NUDGE }));
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setDraft((prev) => normalizeFlyerPhotoPlacement({ ...prev, ox: prev.ox + NUDGE }));
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setDraft((prev) => normalizeFlyerPhotoPlacement({ ...prev, oy: prev.oy - NUDGE }));
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setDraft((prev) => normalizeFlyerPhotoPlacement({ ...prev, oy: prev.oy + NUDGE }));
      }
    };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const updateDraft = useCallback((patch) => {
    setDraft((prev) => normalizeFlyerPhotoPlacement({ ...prev, ...patch }));
  }, []);

  const nudge = (dx, dy) => {
    setDraft((prev) => normalizeFlyerPhotoPlacement({
      ...prev,
      ox: prev.ox + dx,
      oy: prev.oy + dy
    }));
  };

  const onPointerDown = (e) => {
    if (e.button != null && e.button !== 0) return;
    const frame = frameRef.current;
    if (!frame || !photoUrl) return;
    e.preventDefault();
    frame.setPointerCapture?.(e.pointerId);
    dragRef.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startY: e.clientY,
      origX: draft.x,
      origY: draft.y,
      width: frame.clientWidth || 1,
      height: frame.clientHeight || 1
    };
  };

  const onPointerMove = (e) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    updateDraft({
      x: drag.origX - (dx / drag.width) * 100,
      y: drag.origY - (dy / drag.height) * 100
    });
  };

  const endDrag = (e) => {
    const drag = dragRef.current;
    if (!drag || (e?.pointerId != null && drag.pointerId !== e.pointerId)) return;
    dragRef.current = null;
    try {
      frameRef.current?.releasePointerCapture?.(e.pointerId);
    } catch { /* ignore */ }
  };

  const name = String(row?.student_name || 'Student').trim().toUpperCase();
  const roll = String(row?.roll_no || '').replace(/\D/g, '');

  return (
    <div className="br-flyer-photo-editor" onClick={onClose} role="dialog" aria-modal="true" aria-label="Adjust student placement">
      <div className="br-flyer-photo-editor-panel" onClick={(e) => e.stopPropagation()}>
        <header className="br-flyer-photo-editor-head">
          <div>
            <strong>{name}</strong>
            <span>
              {roll ? `Roll ${roll} · ` : ''}
              Move whole block (photo + name + marks) or crop face
            </span>
          </div>
          <button type="button" className="br-flyer-photo-editor-close" onClick={onClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </header>

        <div className="br-flyer-photo-editor-section">
          <div className="br-flyer-photo-editor-section-title">Move student on flyer</div>
          <div className="br-flyer-photo-editor-nudge">
            <button type="button" className="br-flyer-nudge-btn is-up" onClick={() => nudge(0, -NUDGE)} title="Move up" aria-label="Move up">
              <FontAwesomeIcon icon={faArrowUp} />
            </button>
            <button type="button" className="br-flyer-nudge-btn is-left" onClick={() => nudge(-NUDGE, 0)} title="Move left" aria-label="Move left">
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
            <button type="button" className="br-flyer-nudge-btn is-right" onClick={() => nudge(NUDGE, 0)} title="Move right" aria-label="Move right">
              <FontAwesomeIcon icon={faArrowRight} />
            </button>
            <button type="button" className="br-flyer-nudge-btn is-down" onClick={() => nudge(0, NUDGE)} title="Move down" aria-label="Move down">
              <FontAwesomeIcon icon={faArrowDown} />
            </button>
          </div>
          <div className="br-flyer-photo-editor-offset">
            Offset X {draft.ox}px · Y {draft.oy}px
            <span>Arrow keys also nudge · drag on flyer in Adjust mode</span>
          </div>
        </div>

        <div className="br-flyer-photo-editor-section">
          <div className="br-flyer-photo-editor-section-title">Crop face in frame</div>
          <div
            ref={frameRef}
            className="br-flyer-photo-editor-frame"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={endDrag}
            onPointerCancel={endDrag}
            onWheel={(e) => {
              e.preventDefault();
              const delta = e.deltaY > 0 ? -0.08 : 0.08;
              updateDraft({ zoom: draft.zoom + delta });
            }}
          >
            {photoUrl ? (
              <img
                src={photoUrl}
                alt=""
                draggable={false}
                style={{
                  objectPosition: `${draft.x}% ${draft.y}%`,
                  transform: `scale(${draft.zoom})`,
                  transformOrigin: `${draft.x}% ${draft.y}%`
                }}
              />
            ) : (
              <div className="br-flyer-photo-editor-empty">No photo</div>
            )}
          </div>

          <label className="br-flyer-photo-editor-zoom">
            <span>Zoom {draft.zoom.toFixed(2)}×</span>
            <input
              type="range"
              min={1}
              max={2.5}
              step={0.01}
              value={draft.zoom}
              onChange={(e) => updateDraft({ zoom: Number(e.target.value) })}
            />
          </label>
        </div>

        <div className="br-flyer-photo-editor-actions">
          <button
            type="button"
            className="board-results-btn secondary board-results-btn-sm"
            onClick={() => setDraft({ ...DEFAULT_FLYER_PHOTO_PLACEMENT })}
          >
            Reset
          </button>
          <button
            type="button"
            className="board-results-btn primary board-results-btn-sm"
            onClick={() => {
              onSave?.(draft);
              onClose?.();
            }}
          >
            Save placement
          </button>
        </div>
      </div>
    </div>
  );
}
